import { z } from "zod";
import { Accession, Cik, DecimalString, IsoDate, Timestamp } from "./primitives.js";
/**
 * @fileoverview Ask — the owner's questions, answered from the filings with citations.
 */
/**
 * How Ask reads a filing: navigate it, or load it entire.
 *
 * `navigate` reads through tools under a character budget; `whole` preloads the filing
 * into the first message and is the only depth that can answer a question whose answer
 * depends on parts of the filing far from each other.
 *
 * `navigate` is not retrieval over embeddings: `find` matches phrases literally and
 * `outline` reads headings. Embeddings enter only through the corpus-wide `search` tool,
 * which reaches other filings.
 */
export const AskDepth = z.enum(["navigate", "whole"]);
/**
 * What a question is asked of.
 *
 * A `company` scope means the filings that CIK itself filed, whether the company is a
 * parent or a SpinCo; see `server/src/ask/scope.ts`.
 */
export const AskScope = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("library") }),
    z.object({ kind: z.literal("situation"), id: z.string().uuid() }),
    z.object({ kind: z.literal("company"), cik: Cik }),
    z.object({ kind: z.literal("document"), accession: Accession, sequence: z.number().int().min(0) }),
]);
/**
 * A passage the model actually read, recorded by the server as it served it.
 *
 * Not reported by the model: a source here is a log of what the tool layer handed over,
 * so it cannot be fabricated.
 */
export const AskSource = z.object({
    documentId: z.string().uuid(),
    accession: Accession,
    filename: z.string(),
    documentType: z.string().nullable(),
    formType: z.string().nullable(),
    filingDate: IsoDate.nullable(),
    /** Whose filing this is. Named on every source — see `entityWarning`. */
    companyName: z.string().nullable(),
    charStart: z.number().int().nonnegative(),
    charEnd: z.number().int().nonnegative(),
    /** The section it fell in, when the document has an outline. */
    section: z.string().nullable(),
    /** Which tool served it, so a reader can tell a targeted read from a search hit. */
    via: z.string(),
});
/**
 * A quoted passage in the answer, checked against what the model actually read.
 *
 * An unverified quote is marked, not dropped, unlike in extraction: dropping it would
 * leave the prose making the same claim with no citation attached, and "the model could
 * not source this" is information.
 */
export const AskQuote = z.object({
    text: z.string(),
    verified: z.boolean(),
    documentId: z.string().uuid().nullable(),
    accession: Accession.nullable(),
    charStart: z.number().int().nonnegative().nullable(),
    charEnd: z.number().int().nonnegative().nullable(),
});
/** Tokens an answer consumed, and what they are estimated to have cost. */
export const AskUsage = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheWriteTokens: z.number().int().nonnegative(),
    /** Dollars, as a decimal string. */
    estimatedUsd: DecimalString,
});
/**
 * One turn in an Ask thread: the owner's question or the model's answer.
 *
 * `depth`, `model` and `usage` describe how an answer was produced; a question carries none.
 */
export const AskMessage = z.object({
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    role: z.enum(["user", "assistant"]),
    body: z.string(),
    depth: AskDepth.nullable(),
    model: z.string().nullable(),
    sources: z.array(AskSource),
    quotes: z.array(AskQuote),
    /**
     * Set when an answer states dollar figures and the passages behind it span more than
     * one company: a figure quoted verbatim from one entity can be attached to another,
     * and quote verification cannot catch that because nothing about the quote is wrong.
     */
    entityWarning: z.string().nullable(),
    usage: AskUsage.nullable(),
    createdAt: Timestamp,
});
/**
 * A conversation of questions and answers, listed without its messages.
 *
 * `situationId` is set only for a thread asked of one situation; `totalUsd` sums its
 * messages' estimated cost.
 */
export const AskThread = z.object({
    id: z.string().uuid(),
    situationId: z.string().uuid().nullable(),
    title: z.string(),
    messageCount: z.number().int().nonnegative(),
    /** Dollars, as a decimal string. */
    totalUsd: DecimalString,
    createdAt: Timestamp,
    updatedAt: Timestamp,
});
/**
 * The most document text, in characters, a question may read before it has to answer;
 * `null` means unbudgeted.
 *
 * A `navigate` question starts at `NAVIGATE_FIRST_BUDGET` and reaches this ceiling only
 * by escalation, because a model given a larger budget reads more of it.
 */
export const DEPTH_CHAR_BUDGET = Object.freeze({
    navigate: 200_000,
    whole: null,
});
/** The character budget a `navigate` question starts with, before any escalation. */
export const NAVIGATE_FIRST_BUDGET = 60_000;
/**
 * Tool calls a `navigate` question may make before escalation.
 *
 * Raised together with the budget: a question that has run out of one needs more of
 * both, and raising one alone stops it on the other.
 */
export const NAVIGATE_FIRST_STEPS = 8;
/** How many tool calls a depth may make before it has to answer with what it has. */
export const DEPTH_MAX_STEPS = Object.freeze({
    navigate: 14,
    /* Fewer, not more: `whole` has the document already and is not navigating to it. */
    whole: 6,
});
/**
 * Reads a stored `ask_messages.depth` as an `AskDepth`, mapping the retired `locate` and
 * `read` to `navigate`.
 *
 * @param stored the column's value
 * @returns the depth, or `null` for `null` or an unrecognised value
 */
export function normaliseAskDepth(stored) {
    if (stored === null)
        return null;
    if (stored === "locate" || stored === "read")
        return "navigate";
    const parsed = AskDepth.safeParse(stored);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=ask.js.map